/* eslint-disable react-hooks/rules-of-hooks */
import UnifiedPlayer, {
  UnifiedPlayerPlugin,
} from '@tv2-player/unifiedplayer/src/core/UnifiedPlayer';
import yospacePlugin from '@tv2-player/unifiedplayer/src/plugins/Yospace';
import progressConcurrency from '@tv2-player/unifiedplayer/src/plugins/ProgressConcurrency';
import { trackingPlugin } from '@tv2-player/unifiedplayer/src/plugins/Tracking';
import * as Epg from '@tv2-player/unifiedplayer/src/modules/dataProviders/Epg';
import * as Tracking from '@tv2-player/unifiedplayer/src/modules/dataProviders/Tracking';
import { vcrPlugin } from '@tv2-player/unifiedplayer/src/modules/Vcr';
import { nielsenPluginFactory } from '@tv2-player/unifiedplayer/src/modules/Nielsen';
import { CAFPlayerOptions } from '@tv2-player/unifiedplayer';
import AdobeCollectionAPI, {
  type AdobeCollectionAPIOptions,
} from '@tv2-player/unifiedplayer/src/services/AdobeCollectionAPI/AdobeCollectionAPI';
import { metadataPluginFactory } from '@tv2-player/unifiedplayer/src/plugins/Metadata';
import { adobePluginFactory } from '@tv2-player/unifiedplayer/src/modules/Adobe/adobePluginFactory';
import CastAdapter from './CastAdapter';

export const name = 'Unified Player - Cast';

/**
 * Definition for the Play specific properties of the media content object passed into
 * {@link external:UnifiedPlayer.updateMedia}.
 * @typedef  {external:MediaContent}          PlayMediaContent
 * @property {external:TrackConfiguration}    [tracks]   Attributes following HTMLTrackElement spec.
 * @property {TrackingConfiguration}          [tracking] Tracking specific metadata.
 * @property {string}  [ads]                  URL to VAST or VMAP file for playing content with ads.
 * @property {object}  [drm]                  Configuration for protected content.
 * @property {string}  drm.token              Token for the protected content.
 * @property {object}  drm.servers            Specify endpoint configuration for license servers.
 * @property {object}  drm.servers.fairplay   FairPlay specific license configuration.
 * @property {object}  drm.servers.playready  PlayReady specific license configuration.
 * @property {object}  drm.servers.widevine   Widevine specific license configuration.
 * @property {object}  [user]                 Object containing user specific information.
 * @property {string}  [user.accessToken]     Login JWT (used for tracking).
 * @property {boolean} [user.isLoggedIn]      True if the user is logged in.
 * @property {object}  [metadata]             Contains various metadata used for e.g. Tracking.
 * @property {string}  [metadata.title]       Title for the media. Will be shown in the player.
 * @property {string}  [metadata.subtitle]    Subtitle for the media. Will be shown in the player.
 * @property {boolean} [metadata.isLive]      True if the media is live content, false if vod.
 * @property {boolean} [metadata.guid]        Unique identifier for the current media.
 * @property {number}  [metadata.outro]       When content ends (in seconds counted from the end).
 *                                            If omitted, timeline events will not fire.
 */

type Dependencies = {
  cast?: any;
  Adobe?: any;
};

/**
 * Factory function that creates a new UnifiedPlayer instance customized for.
 * [receiver.play.tv2.dk]{@link https://receiver.play.tv2.dk}.
 * @example <caption>index.html</caption>
 * ```html
 * <script rel="preload" src="//www.gstatic.com/cast/sdk/libs/caf_receiver/v3/cast_receiver_framework.js"></script>
 * <script>window.ENVIRONMENT = new URL(location).searchParams.get('env') || 'prod';</script>
 * <script rel="preload" type="text/javascript" src="dist/playReceiver.js"></script>
 * <cast-media-player id="castPlayer"></cast-media-player>
 * <script>window.onload = initPlayReceiver;</script>
 * ```
 *
 * const options = { ... }
 * const player = castPreset(options, cast)
 * @param  {object} [options] Configuration for UnifiedPlayer, adapter and plugins.
 * @param  {object} [options.defaults] Object that is deeply merged with {@link PlayMediaContent}.
 * @param  {object} [dependencies] Optional dependency object as an alternative to using window.
 * @param chromecastVersion
 */
const castPreset = (
  options: CAFPlayerOptions,
  dependencies: Dependencies,
  chromecastVersion: string
) => {
  const { cast = (window as any).cast, Adobe = (window as any).ADBMobile } = dependencies;

  if (!cast) throw Error('Missing dependency cast');
  if (!Adobe) throw Error('Missing dependency Adobe');

  const { adobe: adobeConfig } = options;

  const playerName = 'Unified Player - Cast';

  const adobeCollectionAPIOptions: AdobeCollectionAPIOptions = {
    trackingServer: adobeConfig.mediaHeartbeat.server,
    config: {
      'media.playerName': playerName,
      'analytics.trackingServer': adobeConfig.analytics.server,
      'analytics.reportSuite': adobeConfig.analytics.rsids,
      'visitor.marketingCloudOrgId': adobeConfig.marketingCloud.org,
      'visitor.marketingCloudUserId': options.adobe.visitorMarketingCloudUserId!,
      'visitor.customerIDs': undefined,
      'media.sdkVersion': 'va-api-0.0.0',
      'analytics.enableSSL': true,
    },
  };

  const epgOptions = {
    endpoint: options.epgEndpoint,
    clientPlatform: options.clientPlatform,
  };
  const trackingEnabledPlugins = trackingPlugin({
    epgOptions,
    plugins: [
      adobePluginFactory(
        AdobeCollectionAPI(adobeCollectionAPIOptions) as any,
        (window as any).ADBMobileConfig.mediaHeartbeat.playerName
      ),
      nielsenPluginFactory({
        ...options.nielsen,
        gatewayType: 'Browser',
        consentPromise: Promise.resolve(true),
      }),
    ],
  });

  const plugins: UnifiedPlayerPlugin[] = [
    trackingEnabledPlugins,
    progressConcurrency(options.clientPlatform),
    yospacePlugin(),
    metadataPluginFactory(epgOptions),
  ];

  if (options.enableVcr) {
    plugins.push(vcrPlugin(epgOptions, { Epg, Tracking }));
  }

  return new UnifiedPlayer(new CastAdapter(cast, options), plugins, name);
};
export default castPreset;
export const create = castPreset;
